import { Box, CircularProgress, Fade, Typography } from "@mui/material";
import { Icon } from "@iconify/react";
import { Fragment, useMemo, useState, useEffect } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  DateFilterDrawer,
  DateRange,
  groupByDay,
  HistoryFilterBar,
  HistoryRow,
  STATUSES_BY_GROUP,
  StatusFilterDrawer,
  StatusGroup,
  TypeFilterDrawer,
} from "../../components/pages/history";
import { WebBackButton } from "../../components/shared";
import { getHistory, HistoryOrderType } from "../../services/orders/history";
import { useTelegram } from "../../hooks";
import { useTranslation } from "react-i18next";

export function TransactionHistoryPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("history");

  const { tg } = useTelegram();
  useEffect(() => {
    const handleBack = () => navigate("/");
    tg.BackButton.show();
    tg.onEvent("backButtonClicked", handleBack);
    return () => {
      tg.BackButton.hide();
      tg.offEvent("backButtonClicked", handleBack);
    };
  }, [navigate, tg]);

  const [typeFilter, setTypeFilter] = useState<HistoryOrderType[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusGroup | null>(null);
  const [dateFilter, setDateFilter] = useState<DateRange | null>(null);

  const [openDrawer, setOpenDrawer] = useState<"date" | "type" | "status" | null>(null);
  const closeDrawer = () => setOpenDrawer(null);

  const statuses = statusFilter ? STATUSES_BY_GROUP[statusFilter] : undefined;

  const { data, isLoading, isFetching } = useQuery({
    queryFn: () =>
      getHistory({
        page: 1,
        pageSize: 100,
        types: typeFilter.length > 0 ? typeFilter : undefined,
        statuses,
        dateFrom: dateFilter?.from.toISOString(),
        dateTo: dateFilter?.to.toISOString(),
      }),
    queryKey: ["history", typeFilter, statusFilter, dateFilter?.from.toISOString(), dateFilter?.to.toISOString()],
    keepPreviousData: true,
  });

  const groups = useMemo(() => groupByDay(data?.items ?? []), [data]);

  const dateLocale = i18n.language?.startsWith("en") ? "en-US" : "ru-RU";
  const dateLabel = dateFilter
    ? new Intl.DateTimeFormat(dateLocale, {
        day: "numeric",
        month: "short",
      })
        .format(dateFilter.from)
        .replace(".", "")
    : t("filters.date");
  const typeLabel = typeFilter.length === 0
    ? t("filters.type")
    : t("filters.type_count", { count: typeFilter.length });
  const statusLabel =
    statusFilter === null
      ? t("filters.status")
      : statusFilter === "success"
        ? t("statuses.succeeded")
        : statusFilter === "processing"
          ? t("statuses.pending")
          : t("statuses.failed");

  const isEmpty = !isLoading && (data?.items ?? []).length === 0;

  return (
    <Fade in>
      <Box sx={{ marginBottom: "120px", width: "100%", maxWidth: "100%", overflow: "hidden" }}>
        <WebBackButton to="/" title={t("title")} />

        <HistoryFilterBar
          dateLabel={dateLabel}
          typeLabel={typeLabel}
          statusLabel={statusLabel}
          dateActive={dateFilter !== null}
          typeActive={typeFilter.length > 0}
          statusActive={statusFilter !== null}
          onDateClick={() => setOpenDrawer("date")}
          onTypeClick={() => setOpenDrawer("type")}
          onStatusClick={() => setOpenDrawer("status")}
        />

        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", marginTop: "40px" }}>
            <CircularProgress size={26} />
          </Box>
        )}

        {isEmpty && (
          <EmptyState
            hasFilters={typeFilter.length > 0 || statusFilter !== null || dateFilter !== null}
          />
        )}

        <Box sx={{ marginTop: "20px", display: "grid", gap: "18px" }}>
          {groups.map((group) => (
            <Box key={group.key}>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "text.secondary",
                  marginBottom: "8px",
                  paddingInline: "4px",
                }}
              >
                {group.label}
              </Typography>
              {/* Оборачиваем строки одного дня в единую «плитку» —
                  на глубоко-тёмном фоне без этого HistoryRow сливается
                  с background.default. Внутри — тонкие hairline-
                  разделители в iOS-стиле. */}
              <Box
                sx={(theme) => ({
                  bgcolor: "background.paper",
                  borderRadius: "18px",
                  padding: "4px 8px",
                  border:
                    theme.palette.mode === "light"
                      ? "1px solid rgba(0, 0, 0, 0.05)"
                      : "none",
                  boxSizing: "border-box",
                  width: "100%",
                  maxWidth: "100%",
                  // Обрезаем внутренний контент — если название транзакции
                  // (или сумма) неожиданно окажется длиннее ширины плитки,
                  // это защитит от horizontal-overflow всего лэйаута.
                  overflow: "hidden",
                })}
              >
                {group.items.map((order, idx) => (
                  <Fragment key={order.id}>
                    {idx > 0 && (
                      <Box
                        sx={{
                          height: "1px",
                          bgcolor: "divider",
                          // Отступ слева = ширина иконки (38) + gap (10).
                          marginLeft: "48px",
                        }}
                      />
                    )}
                    <HistoryRow order={order} />
                  </Fragment>
                ))}
              </Box>
            </Box>
          ))}
        </Box>

        {isFetching && !isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", marginTop: "16px", opacity: 0.6 }}>
            <CircularProgress size={18} />
          </Box>
        )}

        <DateFilterDrawer
          open={openDrawer === "date"}
          value={dateFilter?.from ?? null}
          onClose={closeDrawer}
          onApply={setDateFilter}
        />
        <TypeFilterDrawer
          open={openDrawer === "type"}
          value={typeFilter}
          onClose={closeDrawer}
          onApply={setTypeFilter}
        />
        <StatusFilterDrawer
          open={openDrawer === "status"}
          value={statusFilter}
          onClose={closeDrawer}
          onApply={setStatusFilter}
        />
      </Box>
    </Fade>
  );
}

function EmptyState({ hasFilters }: { hasFilters: boolean }) {
  const { t } = useTranslation("history");
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "12px",
        marginTop: "80px",
        padding: "0 24px",
      }}
    >
      <Box
        sx={(theme) => ({
          width: 72,
          height: 72,
          borderRadius: "999px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.06)"
              : "rgba(0, 0, 0, 0.05)",
          color: theme.palette.primary.main,
        })}
      >
        <Icon
          icon={
            hasFilters
              ? "solar:filter-bold-duotone"
              : "solar:clock-square-bold-duotone"
          }
          width={36}
          height={36}
          color="currentColor"
        />
      </Box>
      <Typography sx={{ fontSize: "16px", fontWeight: 600, textAlign: "center" }}>
        {hasFilters ? t("empty_search.title") : t("empty_page.title")}
      </Typography>
      <Typography
        sx={{
          fontSize: "13px",
          color: "text.secondary",
          textAlign: "center",
          maxWidth: 260,
        }}
      >
        {hasFilters ? t("empty_search.subtitle") : t("empty_page.subtitle")}
      </Typography>
    </Box>
  );
}
